import { debounce, clone, noop, isNil, isEmpty, isNaN } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import KEY_CODES from 'commons-forms/constants/keycodes.js';

import 'commons-forms/components/InputText/InputText.scss';

class InputText extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            hasFocus: false,
            stoppedTyping: false,
        };

        this.onBlur = this.onBlur.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        // TODO #fix -
        // It's probable that this debouncing is causing
        // state to be set during render phase
        this.debounce = debounce(() => {
            this.setState({ stoppedTyping: true });
        }, 300);
    }

    onChange(event) {
        const value = event.target.value;
        this.props.valueChange(value, this.props.name, event);
        this.debounce(value);
    }

    onBlur() {
        this.setState({ hasFocus: false });
        this.props.onBlur();
    }

    onFocus() {
        this.setState({ hasFocus: true });
        this.props.onFocus(this.props.name);
    }

    onKeyDown(event) {
        if (
            !this.isControlKeyEvent(event) &&
            this.props.maxLength &&
            event.target &&
            event.target.value.length >= this.props.maxLength
        ) {
            event.preventDefault();
            return;
        }

        // We have to check number fields as well because IE lets the user type non-numerical values
        if (
            ['number', 'tel'].includes(this.props.type) &&
            !this.isControlKeyEvent(event) &&
            !this.isNumberKeyEvent(event)
        ) {
            event.preventDefault();
            return;
        }

        this.props.onKeyDown(event);
    }

    isControlKeyEvent(event) {
        const eventKeyCode = event.which || event.keyCode;
        return (
            [
                KEY_CODES.BACKSPACE,
                KEY_CODES.TAB,
                KEY_CODES.CONTROL,
                KEY_CODES.META_LEFT,
                KEY_CODES.META_RIGHT,
                KEY_CODES.ALT,
                KEY_CODES.LEFT,
                KEY_CODES.RIGHT,
                KEY_CODES.ENTER,
                KEY_CODES.ESCAPE,
            ].includes(eventKeyCode) ||
            event.ctrlKey ||
            event.metaKey ||
            event.altKey
        );
    }

    isNumberKeyEvent(event) {
        // We cannot use 'event.key' as it will return an 'Unidentified' string on Safari iOS
        if (event.key && event.key !== 'Unidentified') {
            return !isNaN(Number(event.key));
        }
        const eventKeyCode = event.which || event.keyCode;
        const isInDigitRange = eventKeyCode >= KEY_CODES.DIGIT_0 && eventKeyCode <= KEY_CODES.DIGIT_9;
        const isInNumberRange = eventKeyCode >= KEY_CODES.NUMPAD_0 && eventKeyCode <= KEY_CODES.NUMPAD_9;
        return isInDigitRange || isInNumberRange;
    }

    render() {
        const inputClasses = this.props.isRequired ? 'input-control required' : 'input-control';
        const parentClasses = clone(this.props.classNames);
        let placeholderValue = this.props.label;

        parentClasses.push('InputText');

        if ((!this.props.isValid && this.state.stoppedTyping) || (!this.props.isValid && this.props.hasBeenSubmit)) {
            parentClasses.push('field-error');
        }

        if (isEmpty(this.props.errorLabel)) {
            parentClasses.push('no-message');
        }
        if ((!isNil(this.props.value) && this.props.value !== '') || this.state.hasFocus) {
            parentClasses.push('not-empty');
            placeholderValue = '';
        }

        return (
            <div className={parentClasses.join(' ')} data-test={this.props.dataTest}>
                <label className="input-label" htmlFor={this.props.name}>
                    {this.props.label}
                </label>
                <input
                    id={`${this.props.name}${this.props.idSuffix}`}
                    type={this.props.type}
                    name={this.props.name}
                    className={inputClasses}
                    placeholder={placeholderValue}
                    value={this.props.value}
                    required={this.props.isRequired}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    onKeyDown={this.onKeyDown}
                    onChange={this.onChange}
                    minLength={this.props.minLength}
                    maxLength={this.props.maxLength}
                    min={this.props.min}
                    max={this.props.max}
                    disabled={this.props.isDisabled}
                />
                {!isEmpty(this.props.errorLabel) && <p className="errors-input">{this.props.errorLabel}</p>}
            </div>
        );
    }
}
InputText.defaultProps = {
    classNames: [],
    errorLabel: '',
    idSuffix: '',
    isDisabled: false,
    isRequired: false,
    hasBeenSubmit: false,
    maxLength: 250,
    minLength: 0,
    onBlur: noop,
    onFocus: noop,
    onKeyDown: noop,
    min: null,
    max: null,
    dataTest: '',
};

InputText.propTypes = {
    classNames: PropTypes.arrayOf(PropTypes.string),
    errorLabel: PropTypes.string,
    idSuffix: PropTypes.string,
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    isValid: PropTypes.bool.isRequired,
    hasBeenSubmit: PropTypes.bool,
    label: PropTypes.string.isRequired,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    onFocus: PropTypes.func,
    type: PropTypes.oneOf(['email', 'text', 'password', 'number', 'tel']).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valueChange: PropTypes.func.isRequired,
    dataTest: PropTypes.string,
};

InputText.displayName = 'InputText';

export default InputText;
